import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants } from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import type {
  FootballFixture,
  FootballSide,
  Maybe,
  PenaltyScore
} from '@ps/web-shared-generated-graphql/sports';
import React from 'react';

import { EventTypeId } from '@swf/sports/types/eventType';
import { getScoreCharacterLength } from '@swf/sports/utils/getScoreCharacterLength';

import ScoreboardWrapper from '../ScoreboardWrapper/ScoreboardWrapper';

import { TXTWCL_Penalties } from './Football.messages.json';
import styles from './Football.scss';

interface Props {
  enrichedParticipants?: EnrichedParticipants;
  eventTime: string;
  footballFixture?: FootballFixture;
  eventId: number;
}

const getPenaltyScore = (
  side: FootballSide,
  penaltyScores?: Maybe<PenaltyScore[]>
): number | undefined =>
  penaltyScores?.filter(
    (penaltyScore) => penaltyScore.side === side && penaltyScore.shotResult === 'SCORE'
  ).length;

const Football: React.FC<Props> = ({
  enrichedParticipants,
  footballFixture,
  eventTime,
  eventId
}) => {
  const [participantHome, participantAway] = enrichedParticipants ?? [];
  const currentScore = footballFixture?.score ?? null;
  const penaltyShootout = footballFixture?.penaltyShootout ?? null;

  const penaltyTakingTeam = penaltyShootout?.nextTeamToShoot;
  const homePenaltyScore = getPenaltyScore('HOME', penaltyShootout?.penaltyScores);
  const awayPenaltyScore = getPenaltyScore('AWAY', penaltyShootout?.penaltyScores);

  return (
    <ScoreboardWrapper eventId={eventId} eventTime={eventTime} eventTypeId={EventTypeId.SOCCER}>
      {penaltyShootout && (
        <div className={styles.pens}>
          <Translation {...TXTWCL_Penalties} />
        </div>
      )}
      <Scoreboard>
        <ScoreboardParticipants>
          <ScoreboardParticipant
            name={participantHome.participant}
            isVisibleMarker={penaltyTakingTeam === 'HOME'}
          />
          <ScoreboardParticipant
            name={participantAway.participant}
            isVisibleMarker={penaltyTakingTeam === 'AWAY'}
          />
        </ScoreboardParticipants>
        <ScoreboardScores>
          {penaltyShootout ? (
            <>
              <ScoreboardScore score={[currentScore?.home ?? null, currentScore?.away ?? null]} />
              <ScoreboardScore score={[homePenaltyScore ?? null, awayPenaltyScore ?? null]} />
            </>
          ) : (
            <ScoreboardScore
              score={[currentScore?.home ?? null, currentScore?.away ?? null]}
              scoreCharacterLength={getScoreCharacterLength(EventTypeId.SOCCER, [
                currentScore?.home ?? 0,
                currentScore?.away ?? 0
              ])}
            />
          )}
        </ScoreboardScores>
      </Scoreboard>
    </ScoreboardWrapper>
  );
};

export default Football;
